import { useEffect, useMemo, useState } from 'react';

interface UsePaginationProps<T> {
  data: T[];
  pageSize: number;
}

interface UsePaginationResult<T> {
  paginatedData: T[];
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
}

export function usePagination<T>({
  data,
  pageSize,
}: UsePaginationProps<T>): UsePaginationResult<T> {
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(data.length / pageSize));

  // Reset to page 1 when the data changes (e.g. filters/search change)
  useEffect(() => {
    setCurrentPage(1);
  }, [data.length]);

  // Clamp current page to valid range
  const safePage = Math.min(currentPage, totalPages);

  const paginatedData = useMemo(
    () => data.slice((safePage - 1) * pageSize, safePage * pageSize),
    [data, safePage, pageSize],
  );

  return {
    paginatedData,
    currentPage: safePage,
    totalPages,
    setCurrentPage,
  };
}
